import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from '../../common/react-redux-hooks';
import CacDynamicTabs from '../enhancedFeatures/CacDynamicTabs';
import GenericDHTableView from '../../common/GenericDHTable/GenericDHTableView';
import {
  getCorrColTableData,
  CAC_TABLE_SUB_HEADER_LABELS,
  CAC_COLUMNS_TABLE_FIRST_COL_HEADER_LABELS,
  getCorrRowTableData,
  CAC_ROWS_TABLE_FIRST_COL_HEADER_LABELS,
  getMainHeaderLabels,
  getCorrAxesTableData,
  CAC_AXES_TABLE_HEADER_LABELS,
  sortCACTable,
  SORT_DIRECTION,
  getSortObj,
} from '../../../util/cacUtils';
import {
  CAC_CORR_COLUMN_TABLE,
  CAC_CORR_ROW_TABLE,
  CAC_CORR_AXES_TABLE,
  CAC_CORR_ACTIVE_TABLE,
  CAC_CORR_TABLE_TYPE,
} from '../CacConstants';
import GenericSHTableView from '../../common/GenericSHTable/GenericSHTableView';
import {
  handleCorrTabSelect,
  updateSortOrder,
} from '../../../actions/cacActions';

const CorrDataView = props => {
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);

  const {
    project: {
      rows, 
      columns,
      corrResponse: { cm,  },
      activeTable: { corrActiveTable },
      chartView,
    },
  } = props;

  let tableArr = [];
  const selectedTabIndex =
    corrActiveTable === CAC_CORR_COLUMN_TABLE.toLowerCase()
      ? 0
      : corrActiveTable === CAC_CORR_ROW_TABLE.toLowerCase()
      ? 1
      : 2;
  let sortObj = props.project.sortData.corrTableType[corrActiveTable] || {};
  const { sortOrder, key, columnIndex } = sortObj;

  const UpdateSortObj = sortObj => {
    const payload = {
      tableType: CAC_CORR_ACTIVE_TABLE,
      module: CAC_CORR_TABLE_TYPE,
      sortObj,
    };
    dispatch(updateSortOrder(payload));
  }

  const handleSort = sortObj => {
    UpdateSortObj(sortObj);
  };

  const handleTabSelect = tabIndex => {
    let table = '';
    switch (tabIndex) {
      case 0: {
        table = CAC_CORR_COLUMN_TABLE.toLowerCase();
        break;
      }
      case 1: {
        table = CAC_CORR_ROW_TABLE.toLowerCase();
        break;
      }
      case 2: {
        table = CAC_CORR_AXES_TABLE.toLowerCase();
        break;
      }
      default: {
        break;
      }
    }
    const payload = {
      table,
      module: CAC_CORR_ACTIVE_TABLE,
    };
    dispatch(handleCorrTabSelect(payload));
  };

  useEffect(() => {
    if (cm && Object.keys(cm).length > 0) {
      switch (corrActiveTable) {
        case CAC_CORR_COLUMN_TABLE.toLowerCase():
          tableArr = getCorrColTableData(cm, columns, chartView);
          sortObj = getSortObj({ cm, chartView, sortObj });
          UpdateSortObj(sortObj);
          break;
        case CAC_CORR_ROW_TABLE.toLowerCase():
          tableArr = getCorrRowTableData(cm, rows, chartView);
          sortObj = getSortObj({ cm, chartView, sortObj });
          UpdateSortObj(sortObj);
          break;
        case CAC_CORR_AXES_TABLE.toLowerCase():
          tableArr = getCorrAxesTableData(cm);
          break;
        default: {
          break;
        }
      }
    }
    if (sortObj.sortOrder && sortObj.sortOrder !== SORT_DIRECTION.NONE) {
      tableArr = sortCACTable(sortObj, tableArr, CAC_TABLE_SUB_HEADER_LABELS);
    }
    setTableData(tableArr);
  }, [
    JSON.stringify(rows),
    JSON.stringify(columns),
    JSON.stringify(cm),
    JSON.stringify(sortObj),
    JSON.stringify(tableArr),
    JSON.stringify(chartView),
    corrActiveTable,
  ]);

  const tableView = () => {
    if (cm && Object.keys(cm).length > 0) {
      return (
        <div className="generic-table-main-wrapper">
          <div className="row expanded">
            <div className="columns large-12 generic-table-child-wrapper-left">
              <div className="row expanded top-controls-wrapper">
                <div className="row expanded generic-table">
                  <div className="columns large-12 padding-left-0 padding-right-0">
                    {corrActiveTable === CAC_CORR_COLUMN_TABLE.toLowerCase() && (
                      <GenericDHTableView
                        tableData={tableData}
                        firstHeaderCol={
                          CAC_COLUMNS_TABLE_FIRST_COL_HEADER_LABELS
                        }
                        firstHeaderColData={tableData.map(item => item.columnName)}
                        mainHeaderCol={getMainHeaderLabels(chartView)}
                        columnHeaders={CAC_TABLE_SUB_HEADER_LABELS}
                        isCorrTable
                        showMainHeader
                        onSort={handleSort}
                        sortColumnIndex={columnIndex}
                        sortKey={key}
                        sortOrder={sortOrder}
                      />
                    )}
                    {corrActiveTable === CAC_CORR_ROW_TABLE.toLowerCase() && (
                      <GenericDHTableView
                        tableData={tableData}
                        firstHeaderCol={CAC_ROWS_TABLE_FIRST_COL_HEADER_LABELS}
                        firstHeaderColData={tableData.map(item => item.rowName)}
                        mainHeaderCol={getMainHeaderLabels(chartView)}
                        columnHeaders={CAC_TABLE_SUB_HEADER_LABELS}
                        isCorrTable
                        showMainHeader
                        onSort={handleSort}
                        sortColumnIndex={columnIndex}
                        sortKey={key}
                        sortOrder={sortOrder}
                      />
                    )}
                    {corrActiveTable === CAC_CORR_AXES_TABLE.toLowerCase() && (
                      <GenericSHTableView
                        data-test="shTableComponent"
                        tableData={tableData}
                        tableHeader={CAC_AXES_TABLE_HEADER_LABELS}
                        isCorrAxesTable
                        onSort={handleSort}
                        sortColumnIndex={columnIndex}
                        sortKey={key}
                        sortOrder={sortOrder}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-test="corrDataViewComponent">
      <CacDynamicTabs
        data-test="dynamicTabsComponent"
        styles={{
          backgroundColor: 'rgba(70, 84, 106, 0)',
          textAlign: 'center',
        }}
        selectedIndex={selectedTabIndex}
        handleSelect={handleTabSelect}
        tabs={[
          {
            title: CAC_CORR_COLUMN_TABLE,
            content: <div>{tableView()}</div>,
          },
          {
            title: CAC_CORR_ROW_TABLE,
            content: <div>{tableView()}</div>,
          },
          {
            title: CAC_CORR_AXES_TABLE,
            content: <div>{tableView()}</div>,
          },
        ]}
      />
    </div>
  );
};

CorrDataView.propTypes = {
  project: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    corrResponse: PropTypes.shape({
      cm: PropTypes.instanceOf(Object),
    }),
    chartView: PropTypes.instanceOf(Array),
    sortData: PropTypes.shape({
      corrTableType: PropTypes.instanceOf(Object),
    }),
    activeTable: PropTypes.shape({
      corrActiveTable: PropTypes.string,
    }),
  }),
};

export default CorrDataView;
